/**
 * Product Card — quick-add size overlay + AJAX add to cart.
 *
 * Modes (data-card-mode on the card root, set by snippets/product-card.liquid):
 *   - atc-direct → "Add to Basket" adds the single variant straight to cart.
 *   - picker     → "Quick add" slides up the quick-add overlay; the user
 *                  picks a size (one option — 5ml/50ml/100ml), then "Add"
 *                  adds the resolved variant.
 *   - pdp-link / sold-out → plain link / disabled button, no JS.
 *
 * After a successful add: dispatches `cart:change` (header listens to update
 * the count bubble) and shows a brief toast. Noted has no cart drawer yet, so
 * the toast + count bubble are the only feedback for now.
 */
(function () {
  'use strict';

  var SEL = {
    card: '[data-product-card]',
    trigger: '[data-card-trigger]',
    picker: '[data-card-picker]',
    close: '[data-card-close]',
    sizeOption: '[data-size-option]',
    add: '[data-card-add]',
    addPrice: '[data-card-add-price]',
    variantSelect: '[data-card-variant-select]',
    price: '[data-card-price]',
    compare: '[data-card-compare]',
    cartBubble: '[data-cart-count-bubble]'
  };

  var ANIM_MS = 300;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getCard(el) {
    return el && el.closest(SEL.card);
  }

  /* ───────────── Quick-add overlay open / close ───────────── */

  function openPicker(card) {
    // Only one open at a time.
    document.querySelectorAll(SEL.card).forEach(function (other) {
      if (other !== card) closePicker(other);
    });
    var picker = card.querySelector(SEL.picker);
    if (!picker) return;
    picker.hidden = false;
    // Next frame so the browser registers the start (translated) position
    // before transitioning to 0 — otherwise it snaps with no slide.
    requestAnimationFrame(function () {
      picker.classList.remove('translate-y-full');
    });
    var trigger = card.querySelector(SEL.trigger);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    card._lastTrigger = trigger;
    // Focus the close control for keyboard users. preventScroll is essential:
    // the picker is still translated below the card here, and a plain focus()
    // scrolls the overflow-clipped card root to reveal it — yanking the product
    // image out of frame for the whole slide.
    var close = picker.querySelector(SEL.close);
    if (close) setTimeout(function () {
      try { close.focus({ preventScroll: true }); } catch (e) { close.focus(); }
      card.scrollTop = 0;
      card.scrollLeft = 0;
    }, 0);
  }

  function isPickerOpen(card) {
    var picker = card && card.querySelector(SEL.picker);
    return picker && !picker.hidden && !picker.classList.contains('translate-y-full');
  }

  function closePicker(card, opts) {
    if (!card) return;
    var picker = card.querySelector(SEL.picker);
    if (!picker || picker.hidden) return;
    picker.classList.add('translate-y-full');
    setTimeout(function () { picker.hidden = true; }, ANIM_MS);
    var trigger = card.querySelector(SEL.trigger);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (opts && opts.restoreFocus && card._lastTrigger) card._lastTrigger.focus();
  }

  /* ───────────── Size selection ───────────── */

  function selectSize(option) {
    if (option.disabled || option.hasAttribute('data-unavailable')) return;
    var card = getCard(option);
    if (!card) return;

    card.querySelectorAll(SEL.sizeOption).forEach(function (o) {
      o.classList.remove('is-selected');
      o.setAttribute('aria-pressed', 'false');
    });
    option.classList.add('is-selected');
    option.setAttribute('aria-pressed', 'true');

    var priceEl = card.querySelector(SEL.addPrice);
    if (priceEl && option.dataset.variantPriceFormatted) {
      priceEl.textContent = option.dataset.variantPriceFormatted;
    }
  }

  function selectedVariantId(card) {
    var sel = card.querySelector(SEL.sizeOption + '.is-selected');
    return sel ? sel.dataset.variantId : null;
  }

  /* ───────────── Triggers ───────────── */

  function handleTrigger(trigger) {
    if (trigger.disabled) return;
    var card = getCard(trigger);
    if (!card) return;
    if (card.dataset.cardMode === 'atc-direct') {
      // A size dropdown, when the card has one, decides what is added.
      var select = card.querySelector(SEL.variantSelect);
      if (select) syncSelect(select);
      if (trigger.dataset.variantId) addToCart(trigger.dataset.variantId, trigger);
    } else if (card.dataset.cardMode === 'picker') {
      // Mobile → the full Add-to-Cart sheet (assets/quick-buy.js), mirroring the
      // PDP buy box. Desktop keeps the card-anchored quick-add overlay.
      if (window.NotedQuickBuy && window.matchMedia('(max-width: 767px)').matches) {
        window.NotedQuickBuy.open(card);
      } else {
        openPicker(card);
      }
    }
  }

  function handleAdd(addBtn) {
    if (addBtn.disabled) return;
    var card = getCard(addBtn);
    var id = card && selectedVariantId(card);
    if (id) addToCart(id, addBtn);
  }

  /* ───────────── AJAX add to cart ───────────── */

  function addToCart(variantId, btn) {
    if (btn) btn.disabled = true;
    // Open the cart drawer immediately (spinner) — the drawer is the add feedback.
    document.dispatchEvent(new CustomEvent('cart:adding'));
    // Bundled section rendering: ask add.js to return the cart-drawer section in
    // the same response, so the drawer renders in one round-trip (cart.js swaps
    // it and reads item_count from the HTML — no separate cart.js fetch).
    var body = { items: [{ id: Number(variantId), quantity: 1 }] };
    var drawer = document.querySelector('[data-cart-drawer]');
    if (drawer && drawer.dataset.sectionId) {
      body.sections = drawer.dataset.sectionId;
      body.sections_url = window.location.pathname;
    }
    fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok) throw res.data;
        document.dispatchEvent(new CustomEvent('cart:change', { detail: { sections: res.data.sections } }));
        if (btn) btn.disabled = false;
        var card = getCard(btn);
        if (card && isPickerOpen(card)) closePicker(card);
      })
      .catch(function (err) {
        console.error('[product-card] add to cart failed', err);
        if (btn) btn.disabled = false;
        document.dispatchEvent(new CustomEvent('cart:add-error', {
          detail: { message: (err && (err.description || err.message)) || '' }
        }));
      });
  }


  /* ───────────── Event delegation ───────────── */
  // NB the wishlist hearts on cards are NOT handled here — assets/swym-wishlist.js
  // (the global Swym bridge) owns every `.swym-add-to-wishlist` theme-wide.

  /* ───────────── Card test B / C (snippets/card-test-variants.liquid) ─────────────
     B: each size button adds its own size in one tap.
     C: the size pills pick a size (50ml to start); Add puts it in the basket.
     Both go through addToCart(), so the basket slides open as the feedback. */

  function addFromB(btn) {
    if (btn.disabled || !btn.dataset.variantId) return;
    btn.classList.add('is-adding');
    addToCart(btn.dataset.variantId, btn);
    setTimeout(function () { btn.classList.remove('is-adding'); }, 1200);
  }

  function selectC(option) {
    if (option.disabled) return;
    var wrap = option.closest('[data-card-test="c"]');
    if (!wrap) return;
    wrap.querySelectorAll('[data-card-c-size]').forEach(function (o) {
      var on = o === option;
      o.classList.toggle('is-selected', on);
      o.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    var add = wrap.querySelector('[data-card-c-add]');
    if (add) add.dataset.variantId = option.dataset.variantId;
  }

  function addFromC(btn) {
    if (btn.disabled) return;
    var wrap = btn.closest('[data-card-test="c"]');
    var picked = wrap && wrap.querySelector('[data-card-c-size].is-selected');
    var id = (picked && picked.dataset.variantId) || btn.dataset.variantId;
    if (id) addToCart(id, btn);
  }

  function onClick(e) {
    var bAdd = e.target.closest('[data-card-b-add]');
    if (bAdd) { addFromB(bAdd); return; }

    var cSize = e.target.closest('[data-card-c-size]');
    if (cSize) { selectC(cSize); return; }

    var cAdd = e.target.closest('[data-card-c-add]');
    if (cAdd) { addFromC(cAdd); return; }

    var trigger = e.target.closest(SEL.trigger);
    if (trigger) { handleTrigger(trigger); return; }

    var option = e.target.closest(SEL.sizeOption);
    if (option) { selectSize(option); return; }

    var add = e.target.closest(SEL.add);
    if (add) { handleAdd(add); return; }

    var close = e.target.closest(SEL.close);
    if (close) { closePicker(getCard(close), { restoreFocus: true }); return; }

    // Outside click closes any open overlay.
    document.querySelectorAll(SEL.card).forEach(function (card) {
      if (isPickerOpen(card) && !card.contains(e.target)) closePicker(card);
    });
  }

  // Select-mode cards (the basket's "We also recommend" rail): the dropdown owns
  // which variant the Add button posts, and the price row follows it.
  //
  // syncSelect() reads what the dropdown ACTUALLY shows, not what the server
  // pre-selected. A browser can put a dropdown back to an earlier choice
  // (form-state restore on reload / back-forward, autofill) without firing
  // `change`, which left the size reading 5ml over the 50ml price and the 50ml
  // id on the button (Grace, 21 Oct). So it runs on load, on pageshow, whenever
  // the drawer re-renders new cards, on change, and again at the moment of add.
  function syncSelect(select) {
    var card = getCard(select);
    var option = select.options[select.selectedIndex];
    if (!card || !option) return;

    var trigger = card.querySelector(SEL.trigger);
    if (trigger) trigger.dataset.variantId = option.value;

    var price = card.querySelector(SEL.price);
    if (price && option.dataset.price) {
      price.textContent = option.dataset.price;
      price.classList.toggle('text-coral-700', !!option.dataset.compare);
      price.classList.toggle('text-black', !option.dataset.compare);
    }

    var compare = card.querySelector(SEL.compare);
    if (compare) {
      compare.textContent = option.dataset.compare || '';
      compare.classList.toggle('hidden', !option.dataset.compare);
    }
  }

  function syncAll(scope) {
    var root = scope || document;
    if (root.matches && root.matches(SEL.variantSelect)) { syncSelect(root); return; }
    if (!root.querySelectorAll) return;
    root.querySelectorAll(SEL.variantSelect).forEach(syncSelect);
  }

  function onChange(e) {
    var select = e.target.closest(SEL.variantSelect);
    if (select) syncSelect(select);
  }

  function onKeydown(e) {
    if (e.key !== 'Escape') return;
    document.querySelectorAll(SEL.card).forEach(function (card) {
      if (isPickerOpen(card)) closePicker(card, { restoreFocus: true });
    });
  }

  function init() {
    document.addEventListener('click', onClick);
    document.addEventListener('change', onChange);
    document.addEventListener('keydown', onKeydown);

    syncAll();
    window.addEventListener('pageshow', function () { syncAll(); });
    // The basket drawer swaps its markup on every cart change (assets/cart.js),
    // so fresh recommend cards arrive after load.
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        for (var j = 0; j < muts[i].addedNodes.length; j++) {
          var n = muts[i].addedNodes[j];
          if (n.nodeType === 1) syncAll(n);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
